import useTemplates from 'src/data-capture/hooks/templates/useTemplates';
import { useCalendarEventMeetingAgenda } from 'src/meetings/hooks/useCalendarEventMeetingAgenda';
import { useGenerateCalendarEventMeetingNote } from 'src/meetings/hooks/useGenerateCalendarEventMeetingNote';
import { showError, showSuccess } from 'src/toasts/toasts';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

/**
 * Generates a meeting note without prompting for attendees or a source: it prefers
 * the meeting agenda when one exists and otherwise falls back to the default note
 * template. Powers the one-click "Generate meeting note" action now that the
 * confirm-attendees modal has been dropped.
 */
export function useGenerateMeetingNoteWithDefaultSource(
  eventId: string,
  hasMeetingAgenda: boolean,
) {
  const { generateMeetingNote, isLoading: isGenerating } =
    useGenerateCalendarEventMeetingNote(eventId);
  const { files: templates, isLoading: templatesLoading } = useTemplates({
    templateType: 'MEETING_NOTE',
  });
  const defaultTemplate = templates.find(template => template.isDefault) ?? templates[0];
  const { meetingAgenda, isLoading: agendaLoading } = useCalendarEventMeetingAgenda({
    eventId,
    enabled: hasMeetingAgenda,
  });

  // Generated agendas are meeting_agenda rows, not DataUploadFiles: the endpoint
  // resolves them via meeting_agenda_id, so send the agenda's UUID, not its fileId.
  const meetingAgendaId = meetingAgenda?.id;
  const canGenerate = meetingAgendaId != null || defaultTemplate != null;
  // The source data must settle before a source can be resolved; callers that
  // fire generation automatically wait on this rather than racing an empty state.
  const isResolvingSource = templatesLoading || (hasMeetingAgenda && agendaLoading);

  const generate = async () => {
    try {
      if (meetingAgendaId != null) {
        await generateMeetingNote({ meetingAgendaId, summarise: true });
      } else if (defaultTemplate) {
        await generateMeetingNote({ templateId: defaultTemplate.id, summarise: true });
      } else {
        showError('No meeting agenda or note template is available to generate from.');
        return;
      }
      showSuccess('Generating meeting note');
    } catch (error) {
      notifyAndLogError(
        'Failed to generate meeting note',
        'Could not generate the meeting note. Please try again.',
        error,
      );
    }
  };

  return { generate, isGenerating, canGenerate, isResolvingSource };
}
